import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import { superFetch } from './superFetch';
import { getPhoenixApiBaseUrl } from './getApiBaseUrl';

export interface FeedbackFormData {
  name: string;
  email: string;
  message: string;
}

export interface SendFeedbackToSlackOptions {
  authContext: AuthenticationContext;
  feedbackData: FeedbackFormData;
  sentryEventId: string;
  apiUrl?: string;
  appName?: 'family-member-web' | 'nursing-home-web' | 'service-provider-web';
}

/**
 * Sends Sentry user feedback information to Slack via the backend API.
 *
 * This function proxies feedback through the Phoenix backend to avoid exposing
 * Slack webhook URLs in the frontend. The backend handles authentication,
 * rate limiting, and Slack message formatting.
 *
 * @param options Configuration including auth context, feedback data, and Sentry event details
 *
 * @example
 * ```typescript
 * import { sendFeedbackToSlack } from '@pflegenavi/shared-frontend/platform';
 *
 * // In Sentry feedbackIntegration onSubmitSuccess callback:
 * onSubmitSuccess: async (data, eventId) => {
 *   await sendFeedbackToSlack({
 *     authContext: yourAuthContext,
 *     feedbackData: data,
 *     sentryEventId: eventId,
 *     appName: 'family-member-web'
 *   });
 * }
 * ```
 */
export async function sendFeedbackToSlack(
  options: SendFeedbackToSlackOptions
): Promise<void> {
  const { authContext, feedbackData, sentryEventId, apiUrl, appName } = options;

  try {
    // eslint-disable-next-line no-console
    console.log('Sending feedback notification via backend API', {
      sentryEventId,
      appName,
    });

    // Gather browser information
    const metadata = {
      user_agent: navigator.userAgent,
      url: window.location.href,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      app_name: appName,
      build_profile: getBuildProfile(),
    };

    // Get Phoenix API base URL
    const baseUrl = getPhoenixApiBaseUrl(apiUrl);

    // Create headers
    const headers = new Headers();
    headers.set('Content-Type', 'application/json');

    // Send feedback to backend endpoint
    // superFetch will automatically include the auth token
    await superFetch(authContext, `${baseUrl}/api/feedback/slack`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        feedback_data: {
          name: feedbackData.name || '',
          email: feedbackData.email || '',
          message: feedbackData.message,
        },
        sentry_event_id: sentryEventId,
        platform: 'web',
        metadata,
      }),
    });

    // eslint-disable-next-line no-console
    console.log('Successfully sent feedback notification');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to send feedback notification:', error);

    // Log error details for debugging
    if (error instanceof Error) {
      // eslint-disable-next-line no-console
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
      });
    }

    // Don't throw - we don't want to disrupt the user experience
    // if Slack notification fails
  }
}

/**
 * Determines the build profile based on the current hostname.
 *
 * @returns 'production' | 'staging' | 'local'
 */
function getBuildProfile(): string {
  const hostname = window.location.hostname;

  if (hostname.includes('staging')) {
    return 'staging';
  }

  if (
    hostname.includes('localhost') ||
    hostname.includes('127.0.0.1') ||
    hostname.includes('0.0.0.0')
  ) {
    return 'local';
  }

  return 'production';
}
